import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { failure, ok, pending } from "@/common/common";
import { StringFieldModel } from "@/common/commonForms";
import {
  confirmAction,
  DCEvent,
  navigate,
  showError,
  showGeneralError,
  showSuccess,
  updateStore,
} from "@/events/eventsDomain";
import { TabProps } from "@/components-v2/Tabs";
import { MenuLinkProps } from "@/components-v2/MenuBar";
import { DropdownMenuItems } from "@/components-v2/Dropdown";

import { templateApiClient } from "@/templates/templateApiClient";
import { HttpStatus, isHttpError } from "@/api/api";
import {
  MapTemplateGraphType,
  mapTemplateLabels,
  ProjectTemplateDTO,
  TemplateDTO,
  TemplateType,
  templateTypeSegments,
} from "@/templates/templateDomain";
import {
  AccountAdminTemplateActions,
  accountAdminTemplateItems,
  defaultPreviewCompany,
  mapTemplateIcons,
  openMapTemplatePanel,
  openTemplatePanel,
  projectTemplateType,
  templateUpdated,
  TemplateViewContext,
  updateSelectedRecipient,
  updateSelectedTemplate,
} from "@/templates/templatePanelDomain";
import {
  mapTemplates,
  matchesSearch,
  ProjectTemplateDialogState,
  projectTemplates,
  savedTemplates,
  TemplateFilter,
  TemplatePreviewCompany,
  TemplateSource,
  TemplateState,
  updateTemplateState,
  withTemplateState,
} from "@/templates/templateStateDomain";
import { ProjectTemplateAccess } from "@/dbot/projectList/projectListDomain";

import { Language } from "@/i18n/languages";

import { IconName } from "@/components-v2/iconMapping";
import { IconProps } from "@/components-v2/Icon";

import { compact } from "lodash";

import { when } from "@/utils/functions";
import { showTemplateSettingsDialog, TemplateAccount } from "@/templates/saveTemplateDomain";

export const projectTemplateFilters = [
  "all",
  "projects",
  "matters",
  "meetings",
  "documents",
] as const;

export type ProjectTemplateFilter = (typeof projectTemplateFilters)[number];

export type MapTemplateFilter = "all" | `${MapTemplateGraphType}`;

const mapTemplateFilters: MapTemplateFilter[] = ["all", "owner", "owns", "dual"];

const matchesMapTemplateFilter = (template: TemplateDTO, filter: MapTemplateFilter): boolean =>
  filter === "all" ||
  (template.data.type === TemplateType.MAP && template.data.graphType === filter);

export const projectTemplateFilterLabels: { [key in ProjectTemplateFilter]: TMessageKey } = {
  all: "enterprise-account.templates.filter.all",
  documents: "enterprise-account.templates.filter.documents",
  matters: "enterprise-account.templates.filter.matters",
  meetings: "enterprise-account.templates.filter.meetings",
  projects: "enterprise-account.templates.filter.projects",
};

export const projectTemplateFilterIcons: { [key in ProjectTemplateFilter]: IconName } = {
  all: "globe",
  projects: "sparkle",
  matters: "gavel",
  meetings: "calendar",
  documents: "files",
};

export const matchesProjectTemplateFilter = (
  template: ProjectTemplateDTO,
  filter: ProjectTemplateFilter,
): boolean => {
  const kind = projectTemplateType(template.data).type;
  switch (filter) {
    case "all":
      return true;
    case "matters":
      return kind === "matter" || kind === "agenda";
    case "meetings":
      return kind === "board-meeting" || kind === "general-meeting";
    case "documents":
      return kind === "document" || kind === "documents";
    case "projects":
      return kind === "project";
  }
};

const mapTemplateFilterIcons: { [key in MapTemplateFilter]: IconProps } = {
  all: { name: "globe" },
  ...mapTemplateIcons,
};

export const templateFilterOf = (
  typeSegment: string | undefined,
  filterSegment: string | undefined,
): TemplateFilter =>
  typeSegment === templateTypeSegments[TemplateType.MAP]
    ? {
        type: TemplateType.MAP,
        filter: mapTemplateFilters.find((it) => it === filterSegment) ?? "all",
      }
    : {
        type: TemplateType.PROJECT,
        filter: projectTemplateFilters.find((it) => it === filterSegment) ?? "all",
      };

export const templateFilterPath = (accountId: string, filter: TemplateFilter): string =>
  compact([
    `/enterprise-account/${accountId}/content`,
    templateTypeSegments[filter.type],
    filter.filter !== "all" && filter.filter,
  ]).join("/");

const allTemplatesOf = (type: TemplateType): TemplateFilter => {
  switch (type) {
    case TemplateType.PROJECT:
      return { type, filter: "all" };
    case TemplateType.MAP:
      return { type, filter: "all" };
  }
};

export const initTemplateState = (
  source: TemplateSource,
  filter: TemplateFilter = allTemplatesOf(TemplateType.PROJECT),
): TemplateState => ({
  source,
  templatesResult: undefined,
  search: new StringFieldModel(),
  filter,
  panelTabId: undefined,
  contents: {},
  selected: undefined,
});

export const openAccountTemplates = (
  accountId: string,
  filter: TemplateFilter,
  companies: TemplatePreviewCompany[],
  templateId?: string,
): DCEvent[] => [
  {
    type: "with-store",
    events: ({ templates }) =>
      templates?.source.type === "account" &&
      templates.source.accountId === accountId &&
      templates.filter.type === filter.type &&
      !templateId
        ? [updateTemplateState(() => ({ filter }))]
        : loadAccountTemplates(accountId, filter, companies, templateId),
  },
];

const loadAccountTemplates = (
  accountId: string,
  filter: TemplateFilter,
  companies: TemplatePreviewCompany[],
  templateId: string | undefined,
): DCEvent[] => {
  const source: TemplateSource = { type: "account", accountId };
  return [
    updateStore({ templates: initTemplateState(source, filter) }),
    ...fetchTemplates(
      source,
      filter.type,
      templateId ? openTemplateOf(filter.type, templateId, companies) : [],
    ),
  ];
};

const openTemplateOf = (
  type: TemplateType,
  templateId: string,
  companies: TemplatePreviewCompany[],
): DCEvent[] => {
  switch (type) {
    case TemplateType.PROJECT:
      return openTemplatePanel(templateId);
    case TemplateType.MAP:
      return [
        withTemplateState(
          (state) =>
            when(
              mapTemplates(state).find((it) => it.id === templateId),
              (template) =>
                openMapTemplatePanel(templateId, defaultPreviewCompany(template, companies)),
            ) ?? [],
        ),
      ];
  }
};

export const closeAccountTemplates = (): DCEvent[] => [updateStore({ templates: undefined })];

export const filteredProjectTemplates = (
  state: TemplateState,
  filter: ProjectTemplateFilter,
): ProjectTemplateDTO[] =>
  projectTemplates(state)
    .filter((template) => matchesProjectTemplateFilter(template, filter))
    .filter(matchesSearch(state));

const filteredMapTemplates = (state: TemplateState, filter: MapTemplateFilter): TemplateDTO[] =>
  savedTemplates(state)
    .filter((template) => matchesMapTemplateFilter(template, filter))
    .filter(matchesSearch(state));

export const listedTemplates = (state: TemplateState): TemplateDTO[] => {
  const filter = state.filter;
  switch (filter.type) {
    case TemplateType.PROJECT:
      return filteredProjectTemplates(state, filter.filter);
    case TemplateType.MAP:
      return filteredMapTemplates(state, filter.filter);
  }
};

export const hasTemplatesInFilter = (state: TemplateState): boolean => {
  const filter = state.filter;
  switch (filter.type) {
    case TemplateType.PROJECT:
      return projectTemplates(state).some((template) =>
        matchesProjectTemplateFilter(template, filter.filter),
      );
    case TemplateType.MAP:
      return savedTemplates(state).some((template) =>
        matchesMapTemplateFilter(template, filter.filter),
      );
  }
};

export type TemplatesEmptyStateProps = { title?: TMessage; description: TMessage };

const noneInFilter = (templateTab: TMessageKey): TemplatesEmptyStateProps => ({
  description: {
    key: "templates.empty-description",
    params: { templateTab: { key: templateTab } },
  },
});

export const templatesEmptyState = (
  state: TemplateState,
  context: TemplateViewContext,
): TemplatesEmptyStateProps => {
  const filter = state.filter;
  switch (filter.type) {
    case TemplateType.PROJECT:
      return savedTemplates(state).length > 0 || context.type === "company"
        ? noneInFilter(projectTemplateFilterLabels[filter.filter])
        : {
            title: { key: "enterprise-account.templates.empty" },
            description: {
              key: "enterprise-account.templates.empty-description",
              params: { accountName: context.accountName },
            },
          };
    case TemplateType.MAP:
      return noneInFilter(
        filter.filter === "all"
          ? "enterprise-account.content.maps"
          : mapTemplateLabels[filter.filter],
      );
  }
};

export const openTemplateEvents = (
  state: TemplateState,
  context: TemplateViewContext,
): ((template: TemplateDTO) => DCEvent[]) | undefined => {
  switch (state.filter.type) {
    case TemplateType.PROJECT:
      return (template) => [
        ...openTemplatePanel(template.id),
        ...(context.type === "company" ? context.companyAdminActions.openScope(template) : []),
      ];
    case TemplateType.MAP:
      return (template) =>
        openMapTemplatePanel(
          template.id,
          context.type === "account"
            ? defaultPreviewCompany(template, context.companies)
            : undefined,
        );
  }
};

export const templateRowMenuItems = (
  state: TemplateState,
  context: TemplateViewContext,
): ((template: TemplateDTO) => DropdownMenuItems) | undefined =>
  context.type === "account"
    ? (template) => accountAdminTemplateItems(template, context.accountAdminActions)
    : undefined;

export const selectTemplateFilter = (filter: TemplateFilter): DCEvent[] => [
  withTemplateState((state) => {
    const isSameType = state.filter.type === filter.type;
    switch (state.source.type) {
      case "account":
        return compact([
          isSameType && updateTemplateState(() => ({ filter })),
          navigate(templateFilterPath(state.source.accountId, filter)),
        ]);
      case "company":
        return isSameType
          ? [updateTemplateState(() => ({ filter }))]
          : [
              updateTemplateState(() => ({ filter, selected: undefined, panelTabId: undefined })),
              ...fetchTemplates(state.source, filter.type),
            ];
    }
  }),
];

const isProjectFilterActive = (state: TemplateState, filter: ProjectTemplateFilter): boolean =>
  state.filter.type === TemplateType.PROJECT && state.filter.filter === filter;

export const projectTemplateFilterMenuItems = (
  state: TemplateState,
  dispatch: (events: DCEvent[]) => void,
): MenuLinkProps[] =>
  projectTemplateFilters.map((filter) => ({
    icon: projectTemplateFilterIcons[filter],
    text: { key: projectTemplateFilterLabels[filter] },
    isActive: isProjectFilterActive(state, filter),
    onActivate: () => dispatch(selectTemplateFilter({ type: TemplateType.PROJECT, filter })),
  }));

export const isTemplateTypeSelected = (state: TemplateState, type: TemplateType): boolean =>
  state.filter.type === type;

export const mapTemplateFilterMenuItems = (
  state: TemplateState,
  dispatch: (events: DCEvent[]) => void,
): MenuLinkProps[] =>
  mapTemplateFilters.map((filter) => ({
    icon: mapTemplateFilterIcons[filter].name,
    iconRotate: mapTemplateFilterIcons[filter].rotate,
    text: {
      key: filter === "all" ? "enterprise-account.templates.filter.all" : mapTemplateLabels[filter],
    },
    isActive: state.filter.type === TemplateType.MAP && state.filter.filter === filter,
    onActivate: () => dispatch(selectTemplateFilter({ type: TemplateType.MAP, filter })),
  }));

export const filterTabProps = (state: TemplateState): TabProps[] =>
  projectTemplateFilters.map((filter) => ({
    id: filter,
    label: { key: projectTemplateFilterLabels[filter] },
    isActive: isProjectFilterActive(state, filter),
    onSelect: selectTemplateFilter({ type: TemplateType.PROJECT, filter }),
  }));

const templatesEvent = (
  source: TemplateSource,
  type: TemplateType,
  callbacks: {
    onSuccess: (res: { data: TemplateDTO[] }) => DCEvent[];
    onFailure: () => DCEvent[];
  },
): DCEvent =>
  templateApiClient.getTemplates(
    source.accountId,
    source.type === "company" ? source.companyId : undefined,
    type,
    callbacks,
  );

export const fetchTemplates = (
  source: TemplateSource,
  type: TemplateType,
  afterLoad: DCEvent[] = [],
): DCEvent[] => [
  updateTemplateState(() => ({ templatesResult: pending() })),
  templatesEvent(source, type, {
    onSuccess: (res) => [
      updateTemplateState((state) =>
        state.filter.type === type ? { templatesResult: ok(res.data) } : {},
      ),
      updateSelectedTemplate(),
      updateSelectedRecipient(),
      ...afterLoad,
    ],
    onFailure: () => [
      updateTemplateState(() => ({ templatesResult: failure() })),
      showGeneralError(),
    ],
  }),
];

export const deleteTemplate = (templateId: string): DCEvent[] => [
  templateApiClient.delete(templateId, {
    onSuccess: (res) => [
      updateTemplateState(() => ({
        templatesResult: ok(res.data),
        selected: undefined,
      })),
      showSuccess({ key: "enterprise-accounts.delete-template-success" }, { autoHide: true }),
    ],
    onFailure: (err) => [
      isHttpError(HttpStatus.CONFLICT, err)
        ? showError({ key: "enterprise-accounts.delete-template-used-on-card" })
        : showGeneralError(),
    ],
  }),
];

export const accountAdminTemplateActions = (
  state: TemplateState,
  account: TemplateAccount,
  language: Language,
): AccountAdminTemplateActions => ({
  openSettings: (summary) =>
    when(
      savedTemplates(state).find((item) => item.id === summary.id),
      (template) =>
        showTemplateSettingsDialog({
          account,
          template,
          language,
          onSaved: (templates) => templateUpdated(template.id, templates),
        }),
    ) ?? [],
  openDelete: (template) =>
    confirmAction({
      title: { key: "enterprise-account.delete-template-dialog.title" },
      text: {
        key: "enterprise-account.delete-template-dialog.description",
        params: { name: template.name },
      },
      confirmLabel: { key: "enterprise-account.delete-template-dialog.confirm" },
      onConfirm: deleteTemplate(template.id),
    }),
});

export const prepToShowUseTemplateDialog = (
  orgNo: string,
  companyId: string,
  access: ProjectTemplateAccess,
): DCEvent[] => {
  const dialog: ProjectTemplateDialogState = {
    title: {
      key: "dbot.template-project.title",
      params: { accountName: access?.enterpriseAccountName },
    },
    scope: undefined,
    isCreating: false,
  };
  const state = initTemplateState({
    type: "company",
    orgNo,
    companyId,
    accountId: access.enterpriseAccountId,
    dialog,
  });
  return [
    updateStore({ templates: state }),
    { type: "show-dialog", dialog: { type: "template-project" } },
    ...fetchTemplates(state.source, TemplateType.PROJECT),
  ];
};
